import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import IconMask from '../IconMask';
import {
  Autocomplete,
  Box,
  colors,
  Grid,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setTheme } from '../../redux/session';
import { getFieldInputSx, getFieldLabelSx } from '../../theme/formStyles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

const colorsList = [
  { label: 'Negro', id: 'black', colorIcon: '#111111' },
  { label: 'Rojo', id: 'red', colorIcon: colors.red[500] },
  { label: 'Rosa', id: 'pink', colorIcon: colors.pink[500] },
  { label: 'Purpura', id: 'purple', colorIcon: colors.purple[500] },
  { label: 'Purpura oscuro', id: 'deepPurple', colorIcon: colors.deepPurple[500] },
  { label: 'Indigo', id: 'indigo', colorIcon: colors.indigo[500] },
  { label: 'Azul', id: 'blue', colorIcon: colors.blue[500] },
  { label: 'Azul claro', id: 'lightBlue', colorIcon: colors.lightBlue[500] },
  { label: 'Cyan', id: 'cyan', colorIcon: colors.cyan[500] },
  { label: 'Olivo', id: 'teal', colorIcon: colors.teal[500] },
  { label: 'Verde', id: 'green', colorIcon: colors.green[500] },
  { label: 'Verde claro', id: 'lightGreen', colorIcon: colors.lightGreen[500] },
  { label: 'Lima', id: 'lime', colorIcon: colors.lime[500] },
  { label: 'Amarillo', id: 'yellow', colorIcon: colors.yellow[700] },
  { label: 'Ambar', id: 'amber', colorIcon: colors.amber[600] },
  { label: 'Naranja', id: 'orange', colorIcon: colors.orange[500] },
  { label: 'Naranja oscuro', id: 'deepOrange', colorIcon: colors.deepOrange[500] },
  { label: 'Cafe', id: 'brown', colorIcon: colors.brown[500] },
  { label: 'Gris', id: 'grey', colorIcon: colors.grey[500] },
  { label: 'Azul-gris', id: 'blueGrey', colorIcon: colors.blueGrey[500] },
];

const modesList = [
  { label: 'Claro', id: 'light' },
  { label: 'Oscuro', id: 'dark' },
];

const roundedList = [
  { label: 'Marcado', id: 'sharp' },
  { label: 'Suave', id: 'soft' },
  { label: 'Redondo', id: 'round' },
];

const sizeList = [
  { label: 'Compacto', id: 'compact' },
  { label: 'Normal', id: 'normal' },
  { label: 'Amplio', id: 'large' },
];

const glassList = [
  { label: 'Activo', id: true },
  { label: 'Inactivo', id: false },
];

const findOption = (options, id, fallbackId) =>
  options.find((item) => item.id === id) || options.find((item) => item.id === fallbackId) || options[0];

const resolveColorId = (themeCustom) => {
  if (themeCustom?.colorId) return themeCustom.colorId;
  if (!themeCustom?.color) return 'black';
  const match = colorsList.find((item) => item.colorIcon === themeCustom?.color?.[500]);
  return match?.id || 'black';
};

const buildFormState = (themeCustom) => ({
  mode: findOption(modesList, themeCustom?.mode, 'light'),
  color: findOption(colorsList, resolveColorId(themeCustom), 'black'),
  rounded: findOption(roundedList, themeCustom?.rounded, 'soft'),
  size: findOption(sizeList, themeCustom?.size, 'normal'),
  glass: findOption(glassList, themeCustom?.glass, true),
});

export default function ThemeButton({ mode = 'button', backgroundColor = 'transparent', drawerOpen = true }) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const themeCustom = useSelector((state) => state.session.theme);
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const [item, setItem] = React.useState(buildFormState(themeCustom));

  React.useEffect(() => {
    setItem(buildFormState(themeCustom));
  }, [themeCustom, open]);

  const fields = [
    { id: 'mode', label: 'Modo de fondo', icon: 'assets/icons/theme.svg', options: modesList },
    { id: 'color', label: 'Color principal', icon: 'assets/icons/paint.svg', options: colorsList },
    { id: 'rounded', label: 'Redondeado', icon: 'assets/icons/overlay.svg', options: roundedList },
    { id: 'size', label: 'Tamaño / aspecto', icon: 'assets/icons/panel.svg', options: sizeList },
    { id: 'glass', label: 'Liquid glass', icon: 'assets/icons/iteration.svg', options: glassList },
  ];

  const getLabel = (value) => (value?.label || '');

  return [
    mode === 'custom' ? (
      <ListItem
        key='theme'
        disablePadding
        sx={{ display: 'block', backgroundColor: 'transparent' }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: drawerOpen ? 'initial' : 'center',
            px: 2.5,
            borderRadius: 2,
            color: open ? theme.palette.primary.main : theme.palette.text.primary,
            backgroundColor: open
              ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(24,39,63,0.10)')
              : 'transparent',
            '&:hover': {
              backgroundColor: open
                ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(24,39,63,0.14)')
                : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(24,39,63,0.06)'),
            },
          }}
          onClick={() => setOpen(true)}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: drawerOpen ? 2.5 : 'auto', justifyContent: 'center' }}>
            <IconMask color={open ? theme.palette.primary.main : theme.palette.text.secondary} url='assets/icons/paint.svg' size={34} />
          </ListItemIcon>
          <ListItemText primary='Diseño' sx={{ opacity: drawerOpen ? 1 : 0 }} />
        </ListItemButton>
      </ListItem>
    ) : (
      <IconButton
        key='iconButton'
        size='large'
        variant='outlined'
        onClick={() => setOpen(true)}
        style={{ backgroundColor: backgroundColor || theme.palette.primary.main }}
      >
        <IconMask color={theme.palette.getContrastText(theme.palette.primary.main)} url='assets/icons/paint.svg' size={25} />
      </IconButton>
    ),

    <Dialog
      key='dialog'
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => setOpen(false)}
      fullScreen={!isMd}
      PaperComponent={Paper}
    >
      <DialogTitle>Personaliza tu experiencia</DialogTitle>
      <DialogContent>
        {fields.map((field, index) => (
          <Grid key={index} direction='row' width={500} maxWidth='100%' container marginBottom={1}>
            <Grid key={field.id} item xs>
              <Autocomplete
                value={item[field.id] || null}
                options={field.options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={getLabel}
                onChange={(e, next) => setItem((prev) => ({ ...prev, [field.id]: next }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Box marginRight={1}>
                          <IconMask color={theme.palette.text.secondary} url={field.icon} size={25} />
                        </Box>
                      ),
                      placeholder: field.label,
                      sx: getFieldInputSx(theme),
                    }}
                    InputLabelProps={{
                      sx: getFieldLabelSx(theme),
                    }}
                    variant='outlined'
                  />
                )}
                renderOption={(props, option) => (
                  <ListItem {...props}>
                    {field.id === 'color' && (
                      <Box p={1} width={30} height={30}>
                        <div
                          style={{
                            backgroundColor: option.colorIcon,
                            borderRadius: '50%',
                            boxShadow: '0px 0px 5px rgba(120,120,120,0.3)',
                            height: '100%',
                            width: '100%',
                          }}
                        />
                      </Box>
                    )}
                    <Typography>{option.label}</Typography>
                  </ListItem>
                )}
              />
            </Grid>
          </Grid>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancelar</Button>
        <Button
          onClick={() => {
            const payload = {
              mode: item.mode?.id || 'dark',
              colorId: item.color?.id || 'blueGrey',
              rounded: item.rounded?.id || 'soft',
              size: item.size?.id || 'normal',
              glass: typeof item.glass?.id === 'boolean' ? item.glass.id : true,
            };
            dispatch(setTheme(payload));
            try {
              localStorage.setItem('design-theme', JSON.stringify(payload));
            } catch (e) {}
            setOpen(false);
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  ];
}
